import {Component, computed, inject, input, signal, viewChild} from '@angular/core';
import {FormControl} from '@angular/forms';

import {Avatar} from 'primeng/avatar';
import {Popover} from 'primeng/popover';
import {Divider} from 'primeng/divider';

import {TextArea} from '../../../../components/text-area/text-area';
import {CommentEntityType, CommentService, CommentSummary, CreateCommentRequest, UserSelect} from '../../../../../api';
import {TodoCommentItem} from '../todo-comment-item/todo-comment-item';
import {TranslatePipe} from '@ngx-translate/core';
import {CommentResource} from '../../../../../api/resources';

export type MentionableTodo = { id: number | null; title: string | null };

type MentionType = 'user' | 'todo' | null;

@Component({
  selector: 'elcia-todo-comment',
  imports: [Avatar, Popover, Divider, TextArea, TodoCommentItem, TranslatePipe],
  templateUrl: './todo-comment.html',
  styleUrl: './todo-comment.scss',
})
export class TodoComment {
  todoId = input.required<number>();
  localTodos = input<MentionableTodo[]>([]);
  users = input<UserSelect[]>([]);
  storeTodos = input<MentionableTodo[]>([]);

  private commentResource = inject(CommentResource);
  private commentService = inject(CommentService);

  private commentsResource = this.commentResource.getComments(CommentEntityType.Todo, this.todoId,
    {defaultValue: {comments: []}},
  );

  comments = computed<CommentSummary[]>(() => this.commentsResource.value().comments);

  isSending = signal(false);
  messageControl = new FormControl('');
  mentionType = signal<MentionType>(null);
  mentionQuery = signal('');
  mentionActiveIndex = signal(-1);
  private mentionTriggerIndex = signal(-1);

  overlayPanel = viewChild.required<Popover>('mentionOverlay');
  textArea = viewChild.required<TextArea>('textAreaComp');

  filteredUsers = computed(() => {
    const query = this.mentionQuery().toLowerCase();
    const all = this.users();
    return query ? all.filter(u => u.fullName.toLowerCase().includes(query)).slice(0, 8) : all.slice(0, 8);
  });

  filteredTodos = computed<MentionableTodo[]>(() => {
    const query = this.mentionQuery().toLowerCase();
    const local = this.localTodos();
    const all = [...local];
    return query ? all.filter(t => (t.title ?? '').toLowerCase().includes(query)).slice(0, 8) : all.slice(0, 8);
  });

  onInput(event: Event) {
    const el = event.target as HTMLTextAreaElement;
    const text = el.value;
    const cursor = el.selectionStart ?? 0;
    const before = text.slice(0, cursor);

    const atMatch = before.match(/@([^@#\n]*)$/);
    const hashMatch = before.match(/#([^@#\n]*)$/);

    const inAtMention = atMatch && !atMatch[1].includes(']');
    const inHashMention = hashMatch && !hashMatch[1].includes(']');

    if (inAtMention) {
      this.mentionType.set('user');
      this.mentionQuery.set(atMatch[1]);
      this.mentionTriggerIndex.set(cursor - atMatch[0].length);
      this.mentionActiveIndex.set(0);
      this.overlayPanel().show(event, el);
    } else if (inHashMention) {
      this.mentionType.set('todo');
      this.mentionQuery.set(hashMatch[1]);
      this.mentionTriggerIndex.set(cursor - hashMatch[0].length);
      this.mentionActiveIndex.set(0);
      this.overlayPanel().show(event, el);
    } else {
      this.mentionType.set(null);
      this.mentionActiveIndex.set(-1);
      this.overlayPanel().hide();
    }
  }

  onKeydown(event: KeyboardEvent) {
    const type = this.mentionType();

    if (type && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const list = type === 'user' ? this.filteredUsers() : this.filteredTodos();
      const len = list.length;
      if (!len) return;
      const current = this.mentionActiveIndex();
      this.mentionActiveIndex.set(event.key === 'ArrowDown' ? (current + 1) % len : (current - 1 + len) % len);
      return;
    }

    if (type && event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const idx = this.mentionActiveIndex();
      if (type === 'user') {
        const user = this.filteredUsers()[idx];
        if (user) this.selectUser(user);
      } else {
        const todo = this.filteredTodos()[idx];
        if (todo) this.selectTodo(todo);
      }
      return;
    }

    if (type && event.key === 'Escape') {
      this.mentionType.set(null);
      this.mentionActiveIndex.set(-1);
      this.overlayPanel().hide();
      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  selectUser(user: UserSelect) {
    this.insertMention(`@[${user.fullName}]`);
  }

  selectTodo(todo: MentionableTodo) {
    this.insertMention(`#[${todo.title ?? todo.id}]`);
  }

  sendMessage() {
    const text = (this.messageControl.value ?? '').trim();
    if (!text || this.isSending()) return;

    this.isSending.set(true);
    const request = {
      content: text,
      entityId: this.todoId(),
      entityType: CommentEntityType.Todo
    } as CreateCommentRequest

    this.commentService.createComment(request).subscribe({
      next: () => {
        this.messageControl.reset('');
        this.commentsResource.reload();
        this.isSending.set(false);
      },
      error: () => this.isSending.set(false),
    });
  }

  private insertMention(mention: string) {
    const el = this.textArea().nativeEl;
    const text = this.messageControl.value ?? '';
    const cursor = el.selectionStart ?? 0;
    const triggerIndex = this.mentionTriggerIndex();
    const newText = text.slice(0, triggerIndex) + mention + ' ' + text.slice(cursor);

    this.messageControl.setValue(newText);
    this.overlayPanel().hide();
    this.mentionType.set(null);

    setTimeout(() => {
      const pos = triggerIndex + mention.length + 1;
      el.selectionStart = pos;
      el.selectionEnd = pos;
      el.focus();
    });
  }
}
